import { useCallback } from 'react';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from 'react-query';
import { useTrackProspect, useTrackQuoteCreation } from '@app/analytics';
import {
  API_URL,
  queryCreate,
  queryFetch,
  queryGet,
  queryReplace,
  queryUpdate,
} from './fetch';
import {
  OwnRecruitmentOptions,
  SessionFormat,
  StudySubject,
  StudyType,
} from './session';
import { Product } from './plan';
import { PaymentMethod } from './paymentMethod';
import { getAnalyticsStdParamsFromQuote } from './utils/analyticsUtils';
import { formatSiret } from '@app/i18n';

export interface QuoteFromOfferDto {
  offerIds: string[];
  duration: number;
  durationDisplayed?: string;
  numberOfParticipantRequired: number;
  prospectId?: string;
  organizationId?: string;
  studyType: StudyType;
  studySubject: StudySubject;
  format?: SessionFormat;
}

export interface QuoteFromPlanDto {
  duration: number;
  numberOfParticipantRequired: number;
  studyType: StudyType;
  studySubject: StudySubject;
  format?: SessionFormat;
}

export interface QuoteInformation {
  source?: string;
  studyType?: StudyType;
  studySubject?: StudySubject;
  groupMeeting?: boolean;
  format?: SessionFormat;
  targetingOfferId?: string;
  supportOfferId?: string;
  optionsOfferIds?: string[];
  offerIds?: string[];
  priceThierId?: string;
  duration?: number;
  durationDisplayed?: string;
  countryKeys: string[];
  numberOfParticipantRequired?: number;
  haveQuote?: boolean;
  quoteRef?: string;
  prospectId?: string;
  studyId?: string;
  planId?: string;
  billingMethod?: BillingMethod;
  billingCountry?: string;
  organizationId?: string;
  ownRecruitmentOption?: OwnRecruitmentOptions;
}

export enum BillingMethod {
  CREDIT_CARD = 'CREDIT_CARD',
  SEND_INVOICE = 'SEND_INVOICE',
  ORG_CREDIT = 'ORG_CREDIT',
}

export interface Address {
  line1: string;
  line2?: string;
  postalCode: string;
  city: string;
  country: string;
}

export interface BillingInformation {
  id: string;
  organizationId: string;
  stripeId?: string;
  providerId?: string;
  providerAddressId?: string;
  provider?: string;

  billingOrganization?: string;
  routingId?: string;
  email?: string;
  billingAddress?: Address;
  deliveryAddress?: Address;
  vatNumber?: string | null;
  locale: string;

  defaultPaymentMethod?: PaymentMethod;
  defaultPaymentMethodId?: string;

  defaultStudyBillingMethod?: BillingMethod;
  defaultSubscriptionBillingMethod?: BillingMethod;
  billingInformationValidated: boolean;
  billingInstructions?: string;
  requirePurchaseOrderNumber?: boolean;
  requireUseOfSpecificPlatform?: boolean;
  extraBillingEmails?: string[];
  preventAutomaticBilling?: boolean;

  createdAt: Date;
  updatedAt: Date;
}

export const enum RoutingIdType {
  SIRET = 'siret',
}

export const getRoutingIdType = (billingInformation: BillingInformation) => {
  const country = billingInformation.billingAddress?.country || '';
  if (['FR', 'MC'].includes(country)) {
    return RoutingIdType.SIRET;
  }
  return undefined;
};

export const formatRoutingId = (type?: RoutingIdType, routingId?: string) => {
  if (type === RoutingIdType.SIRET) {
    return formatSiret(routingId);
  }
  return routingId;
};

export enum QuoteStatus {
  REQUESTED = 'REQUESTED',
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  ACCEPTED = 'ACCEPTED',
  CANCELED = 'CANCELED',
}

export interface QuoteProduct {
  id: string;
  product?: Product;
  productId?: string;
  quote?: Quote;
  quoteId: string;
  countryPriceGroupId?: string;
  forRecruitmentByClient?: boolean;
  title?: string;
  unitPrice?: number;
  quantity?: number;
  createdAt: Date;
}

export interface Quote {
  id: string;
  ref?: string;
  stripeId?: string;
  offerIds: string[];
  organizationId?: string;
  clientId?: string;
  studyType: StudyType;
  studySubject: StudySubject;
  format?: SessionFormat;
  products?: QuoteProduct[];
  numberOfParticipantRequired: number;
  duration: number;
  durationDisplayed?: string;
  groupMeeting?: boolean;
  status: QuoteStatus;
  prospect: Prospect;
  prospectId?: string;
  studyId?: string;
  billingCountry?: string;
  countryKeys?: string[];
  priceTierId?: string;
  headerText?: string;
  footerText?: string;
  missingStudyFields: string[];
  createdAt: Date;
  updatedAt: Date;
}

export const canLaunchStudyFromQuote = (quote?: Quote) =>
  quote?.status === QuoteStatus.ACCEPTED &&
  !quote.studyId &&
  !quote.missingStudyFields?.length;

export enum ProspectActionEnum {
  REQUEST_QUOTE = 'REQUEST_QUOTE',
  REQUEST_DEMO = 'REQUEST_DEMO',
  SAVE_QUOTE = 'SAVE_QUOTE',
  CREATE_ACCOUNT = 'CREATE_ACCOUNT',
  REQUEST_DISCUSS = 'REQUEST_DISCUSS',
}

export class StudySetupInfo {
  studySubject?: StudySubject;
  studyType?: StudyType;
  numberOfParticipantRequired?: number;
  format?: SessionFormat;
  duration?: number;
}

export interface ProspectAction {
  id: string;
  action: ProspectActionEnum;
  prospectId: string;
  studySetupInfo?: StudySetupInfo;
  quoteId?: string;
  date: Date;
}

export interface Prospect {
  id: string;
  lastAction: ProspectActionEnum;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  jobTitle: string;
  companyName: string;
  locale: string;
  actions: ProspectAction[];
  accountId?: string;
  organizationId?: string;
  billingMethod?: string;
  billingCountryCode?: string;
  createdAt: Date;
  updatedAt: Date;
}

export const useGenerateQuoteFromOffers = () => {
  const trackQuote = useTrackQuoteCreation();
  return useMutation<Quote, any, QuoteFromOfferDto>(queryCreate('/v2/quotes'), {
    onSuccess: (quote: Quote) => {
      trackQuote(quote, getAnalyticsStdParamsFromQuote(quote));
    },
  });
};

export const useGenerateQuoteFromPlan = (planId?: string) => {
  const trackQuote = useTrackQuoteCreation();
  return useMutation<Quote, any, QuoteFromPlanDto>(
    queryCreate(`/v2/plans/${planId}/quotes`),
    {
      onSuccess: (quote: Quote) => {
        trackQuote(quote, getAnalyticsStdParamsFromQuote(quote));
      },
    },
  );
};

export interface DraftQuoteDto {
  organizationId?: string;
  clientId?: string;
  prospectId?: string;
  offerIds?: string[];
  priceTierId?: string;
  studyType?: StudyType;
  studySubject?: StudySubject;
  format?: SessionFormat;
  numberOfParticipantRequired: number;
  duration: number;
  durationDisplayed?: string;
  groupMeeting?: boolean;
  countryKeys?: string[];
  billingMethod?: BillingMethod;
  billingCountry?: string;
}

export interface QuoteLineDto {
  productId?: string;
  title?: string;
  unitPrice?: number;
  quantity: number;
  countryPriceGroupId?: string;
}

export interface SaveQuoteProductsDto {
  lines: QuoteLineDto[];
  replacedIds?: string[];
  numberOfParticipantRequired?: number;
  headerText?: string;
  footerText?: string;
}

/** Empty quote created by an admin, nothing is pushed to stripe */
export const useCreateDraftQuote = () => {
  return useMutation<Quote, any, DraftQuoteDto>(
    queryCreate('/v2/quotes/draft'),
  );
};

export const useSaveQuoteProducts = (refOrId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<Quote, any, SaveQuoteProductsDto>(
    queryReplace(`/v2/quotes/${refOrId}/products`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['quote', refOrId]);
      },
    },
  );
};

export const useUpdateQuoteStatus = (refOrId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<Quote, any, { status: QuoteStatus }>(
    queryUpdate(`/v2/quotes/${refOrId}/status`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['quote', refOrId]);
      },
    },
  );
};

export interface CreateStudyFromQuoteDto {
  format?: SessionFormat;
  groupMeeting?: boolean;
  durationDisplayed?: string;
  ownRecruitmentOption?: OwnRecruitmentOptions;
}

export const useCreateStudyFromQuote = (refOrId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, any, CreateStudyFromQuoteDto>(
    queryCreate(`/v2/quotes/${refOrId}/studies`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['quote', refOrId]);
      },
    },
  );
};

/**
 * The quote as a PDF, generated by the api from the quote itself. `locale` and `vat` are the two settings
 * of the admin preview that are not stored on the quote, they travel with the request.
 */
export const openQuoteDocument = (
  refOrId: string,
  locale: string,
  vat: boolean,
) => {
  const params = new URLSearchParams({ locale, vat: vat ? 'true' : 'false' });

  window.open(`${API_URL}/v2/quotes/${refOrId}/document?${params}`, '_blank');
};

export const openClientQuoteDocument = (refOrId: string) => {
  window.open(`${API_URL}/v2/quotes/${refOrId}/document`, '_blank');
};

/**
 * Who the quote is addressed to (admin only endpoint). A quote drafted for an existing organization
 * carries no prospect: the api then answers the organization and its owner instead, so there is no
 * prospect id to return.
 */
export interface QuoteProspectInfo {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  companyName?: string;
  locale?: string;
  billingCountryCode?: string;
}

export const useQuoteProspect = (refOrId?: string) => {
  return useQuery<QuoteProspectInfo | null>(
    ['quote', refOrId, 'prospect'],
    queryGet(`/v2/quotes/${refOrId}/prospect`),
    { enabled: !!refOrId },
  );
};

/**
 * `locale` asks the api for the product titles of the quote in that language,
 * the same way the product catalog is translated.
 */
export const useQuote = (refOrId?: string, locale?: string) => {
  return useQuery<Quote>(
    locale ? ['quote', refOrId, locale] : ['quote', refOrId],
    () =>
      queryFetch<Quote>({
        url: `${API_URL}/v2/quotes/${refOrId}`,
        method: 'GET',
        headers: locale ? { 'Accept-Language': locale } : undefined,
      }),
    {
      enabled: !!refOrId,
    },
  );
};

export const useCreateProspect = () => {
  const trackProspect = useTrackProspect();

  return useMutation<Prospect, any, Partial<Prospect>>(
    queryCreate('/v2/prospects'),
    {
      onSuccess: (p) => {
        if (!p.accountId) {
          trackProspect(p);
        }
      },
    },
  );
};

export const useAddActionToProspect = () => {
  const mutate = useCallback(
    (action: Partial<ProspectAction>) =>
      queryCreate(`/v2/prospects/${action.prospectId}/actions`)(action),
    [],
  );
  return useMutation<Prospect, any, Partial<ProspectAction>>(mutate);
};

export const useBillingInformationsByOrga = (orgaId?: string) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (info: BillingInformation) => {
      queryClient.setQueryData(['billing-informations', info.id], info);
    },
    [queryClient],
  );

  return useQuery<BillingInformation>(
    ['organizations', orgaId, 'billing-informations'],
    queryGet(`/v2/organizations/${orgaId}/billing-informations`),
    { enabled: !!orgaId, onSuccess },
  );
};

export const useBillingInformation = (id?: string) => {
  return useQuery<BillingInformation>(
    ['billing-informations', id],
    queryGet(`/v2/billing-informations/${id}`),
    { enabled: !!id },
  );
};

export const useUpdateBillingAddressDetails = (id?: string) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (info: BillingInformation) => {
      queryClient.refetchQueries([
        'organizations',
        info.organizationId,
        'billing-informations',
      ]);
      queryClient.setQueryData(['billing-informations', info.id], info);
      queryClient.invalidateQueries(['billing-informations', info.id, 'check']);
    },
    [queryClient],
  );

  return useMutation<
    BillingInformation,
    undefined,
    Partial<BillingInformation>
  >(queryUpdate(`/v2/billing-informations/${id}/address-details`), {
    onSuccess,
  });
};

export const useUpdateBillingAdminInstruction = (id?: string) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (info: BillingInformation) => {
      queryClient.refetchQueries([
        'organizations',
        info.organizationId,
        'billing-informations',
      ]);
      queryClient.setQueryData(['billing-informations', info.id], info);
    },
    [queryClient],
  );

  return useMutation<
    BillingInformation,
    undefined,
    Partial<BillingInformation>
  >(queryUpdate(`/v2/billing-informations/${id}/admin-instructions`), {
    onSuccess,
  });
};

const useOnBillingInformationChangeSuccess = <TVariables = any, TContext = any>(
  onSuccess?: (
    data: BillingInformation,
    variables: TVariables,
    context: TContext | undefined,
  ) => Promise<unknown> | void,
) => {
  const queryClient = useQueryClient();
  return useCallback(
    (info: BillingInformation, variables: TVariables, context: TContext) => {
      queryClient.setQueryData(
        ['organizations', info.organizationId, 'billing-informations'],
        info,
      );
      queryClient.setQueryData(['billing-informations', info.id], info);
      if (onSuccess) {
        onSuccess(info, variables, context);
      }
    },
    [queryClient, onSuccess],
  );
};

export const useUpdateBillingMethod = (
  id?: string,
  options: UseMutationOptions<
    BillingInformation,
    undefined,
    { billingMethod: BillingMethod }
  > = {},
) => {
  const onSuccess = useOnBillingInformationChangeSuccess(options?.onSuccess);
  return useMutation<
    BillingInformation,
    undefined,
    { billingMethod: BillingMethod }
  >(queryUpdate(`/v2/billing-informations/${id}/billing-method`), {
    ...options,
    onSuccess,
  });
};

export const useUpdateBillingInformationProvider = (id?: string) => {
  const onSuccess = useOnBillingInformationChangeSuccess();
  return useMutation<
    BillingInformation,
    undefined,
    {
      provider?: string;
      providerId?: string | null;
      providerAddressId?: string | null;
    }
  >(queryUpdate(`/v2/billing-informations/${id}/provider`), { onSuccess });
};

export const useValidateBillingInformation = (id?: string) => {
  const onSuccess = useOnBillingInformationChangeSuccess();
  return useMutation<BillingInformation>(
    queryUpdate(`/v2/billing-informations/${id}/validate`),
    { onSuccess },
  );
};

export interface BillingInformationCheckResult {
  siret?: {
    success: boolean;
    message: string;
    siret?: string;
    siren?: string;
    name?: string;
    address: {
      line1?: string;
      line2?: string;
      postalCode?: string;
      city?: string;
      country?: string;
    };
    stillOpen?: boolean;
  };
  vat?: {
    success: boolean;
    message: string;
    vatNumber?: string;
    name?: string;
    address?: string;
  };
}

export const useBillingInformationCheck = (id?: string) => {
  return useQuery<BillingInformationCheckResult>(
    ['billing-informations', id, 'check'],
    queryGet(`/v2/billing-informations/${id}/check`),
    { enabled: !!id },
  );
};

export const useBillingInformationRemoteAddresses = (
  bi?: BillingInformation,
) => {
  const id = bi?.id;
  return useQuery<BillingInformation[]>(
    ['billing-informations', id, 'remote-addresses'],
    queryGet(`/v2/billing-informations/${id}/remote-addresses`),
    { enabled: !!id && !!bi?.providerId },
  );
};

export const useSearchBillingInformation = (
  id: string,
  vat?: string,
  routingId?: string,
) => {
  return useQuery<BillingInformation[]>(
    ['billing-informations', id, 'search', { vat, routingId }],
    () =>
      queryCreate(`/v2/billing-informations/${id}/search`)({
        vat,
        routingId,
      }),
    { enabled: !!id },
  );
};

export type QuoteHeaderBlock = 'sender' | 'client' | 'quote.dates';

export type QuoteFooterBlock = 'cgu';

export type QuoteBlock = QuoteHeaderBlock | QuoteFooterBlock;

export interface QuoteTextInfo {
  template: string;
  blocks: Partial<Record<QuoteBlock, string>>;
  requiredBlocks: QuoteBlock[];
}

export interface QuoteTexts {
  header: QuoteTextInfo;
  footer: QuoteTextInfo;
}

export interface RenderedQuoteTexts {
  header: string;
  footer: string;
}

export const useQuoteTexts = (refOrId?: string, locale?: string) =>
  useQuery<QuoteTexts>(
    ['quotes', refOrId, 'texts', locale],
    queryGet(`/v2/quotes/${refOrId}/texts?locale=${locale || ''}`),
    { enabled: !!refOrId, staleTime: Infinity },
  );

export const useRenderQuoteTexts = (refOrId?: string) =>
  useMutation<
    RenderedQuoteTexts,
    Error,
    { header?: string; footer?: string; locale?: string }
  >(queryCreate(`/v2/quotes/${refOrId}/texts/render`));

export const missingQuoteBlocks = (
  html: string,
  info?: QuoteTextInfo,
): QuoteBlock[] => {
  if (!info) {
    return [];
  }

  const written = html.trim() || info.template;

  return info.requiredBlocks.filter(
    (block) => !written.includes(`data-var="${block}"`),
  );
};
